/**
 * usePersonalSyncStore
 *
 * Always connects — caller must mount/unmount the consumer to control lifecycle
 * (e.g. only render the consuming component when mode === "edit"). Mounting this
 * hook with a stale/null mux would leave useSync glued to the wrong socket.
 *
 * userInfo MUST be passed when the consumer wants to publish presence (cursor,
 * camera, name) into this room. Students pass their own userInfo so the teacher's
 * Student Work view can mirror the student's viewport via editor.startFollowingUser.
 * Teachers viewing a student's room may omit it — their presence in someone else's
 * personal room is not useful and would clutter the view.
 */

import { useCallback } from "react";
import { type TLStore, type TLPresenceUserInfo } from "tldraw";
import { useSync } from "@tldraw/sync";
import { SPACES_SHAPE_UTILS, noopAssets } from "../constants";
import type { SpaceSyncMultiplexer } from "../spaceSyncMultiplexer";

export function usePersonalSyncStore(
  mux: SpaceSyncMultiplexer,
  userId: string,
  userInfo?: TLPresenceUserInfo,
): TLStore | null {
  const connect = useCallback(() => mux.getChannel(userId), [mux, userId]);
  const result = useSync({
    connect,
    assets: noopAssets,
    userInfo,
    shapeUtils: SPACES_SHAPE_UTILS,
  });
  return result.status === "synced-remote" ? (result.store ?? null) : null;
}
